/**
 * Portal-Monatskalender — Datums-/Raster-Logik (kein CalDAV, keine NC-App).
 * Woche beginnt Montag. Lokales Kalenderdatum, kein UTC-Tagesversatz.
 *
 * Klasse KALENDER_TILE_SEAT_OR_SKIP: Kacheln mit Überschrift, PB-Persistenz.
 * Klasse KALENDER_DAY_OPEN_LIST: Tag-Klick öffnet Tagesansicht mit Liste,
 * Anlegen nur Plus/Slot-Popup. localStorage-Helfer unten sind kein Produkt-Store.
 * Klasse KALENDER_SEARCH_CLICK: Suchfeld filtert geladene PB-Termine des Sitzes.
 */

export const WEEKDAY_LABELS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export const MONTH_LABELS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface CalendarCell {
  iso: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

export interface CalendarFristRef {
  start_datum: string;
  ende_datum: string;
  frist_typ: string;
}

export function isoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIsoDateLocal(iso: string): Date | null {
  const m = ISO_DAY_RE.exec(String(iso || '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

export function sliceIsoDay(value: string | undefined | null): string {
  const m = ISO_DAY_RE.exec(String(value || '').trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

export function isValidOptionalTime(value: string | undefined | null): boolean {
  const t = String(value || '').trim();
  if (!t) return true;
  return TIME_RE.test(t);
}

/** Eine Uhrzeit oder eine Spanne HH:MM-HH:MM, mindestens ein Viertel. */
export function isValidCalendarStoredTime(value: string | undefined | null): boolean {
  const t = String(value ?? '').trim();
  if (!t) return true;
  if (TIME_RE.test(t)) return true;
  const range = /^(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(t);
  if (!range || !TIME_RE.test(range[1]) || !TIME_RE.test(range[2])) return false;
  const [sh, sm] = range[1].split(':').map(Number);
  const [eh, em] = range[2].split(':').map(Number);
  return eh * 60 + em - (sh * 60 + sm) >= 15;
}

export function mondayIndexFromJsDay(jsDay: number): number {
  return (jsDay + 6) % 7;
}

export function addCalendarMonths(
  year: number,
  monthIndex: number,
  delta: number,
): { year: number; monthIndex: number } {
  const d = new Date(year, monthIndex + delta, 1);
  return { year: d.getFullYear(), monthIndex: d.getMonth() };
}

export function addCalendarDays(iso: string, delta: number): string {
  const d = parseIsoDateLocal(iso);
  if (!d) return '';
  d.setDate(d.getDate() + delta);
  return isoDateLocal(d);
}

/** Montag bis Sonntag der Woche, die `iso` enthält. Immer sieben Tage. */
export function calendarWeekIsos(iso: string): string[] {
  const parsed = parseIsoDateLocal(iso);
  if (!parsed) return [];
  const monday = addCalendarDays(isoDateLocal(parsed), -mondayIndexFromJsDay(parsed.getDay()));
  if (!monday) return [];
  return Array.from({ length: 7 }, (_, i) => addCalendarDays(monday, i));
}

export function buildMonthGrid(
  year: number,
  monthIndex: number,
  today: Date = new Date(),
): CalendarCell[] {
  const first = new Date(year, monthIndex, 1);
  const startOffset = mondayIndexFromJsDay(first.getDay());
  const start = new Date(year, monthIndex, 1 - startOffset);
  const todayIso = isoDateLocal(today);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const iso = isoDateLocal(d);
    const jsDay = d.getDay();
    cells.push({
      iso,
      day: d.getDate(),
      inMonth: d.getMonth() === monthIndex,
      isToday: iso === todayIso,
      isWeekend: jsDay === 0 || jsDay === 6,
    });
  }
  return cells;
}

export function formatMonthHeading(year: number, monthIndex: number): string {
  const name = MONTH_LABELS_DE[monthIndex] ?? '';
  return name ? `${name} ${year}` : String(year);
}

export function formatLongDeDate(iso: string): string {
  const d = parseIsoDateLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString('de-AT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function fristTouchesIsoDate(frist: CalendarFristRef, iso: string): boolean {
  const day = sliceIsoDay(iso);
  if (!day) return false;
  return sliceIsoDay(frist.start_datum) === day || sliceIsoDay(frist.ende_datum) === day;
}

export function fristLabelOnDate(frist: CalendarFristRef, iso: string): string {
  const day = sliceIsoDay(iso);
  const start = sliceIsoDay(frist.start_datum);
  const end = sliceIsoDay(frist.ende_datum);
  const typ = (frist.frist_typ || 'Frist').trim() || 'Frist';
  if (start === day && end === day) return typ;
  if (end === day) return `${typ} (Ende)`;
  if (start === day) return `${typ} (Beginn)`;
  return typ;
}

export interface DayMarkerCounts {
  events: number;
  fristen: number;
}

export function countDayMarkers(
  iso: string,
  eventDates: Iterable<string>,
  fristen: CalendarFristRef[],
): DayMarkerCounts {
  const day = sliceIsoDay(iso);
  let events = 0;
  for (const d of eventDates) {
    if (sliceIsoDay(d) === day) events += 1;
  }
  const fristenCount = fristen.filter((f) => fristTouchesIsoDate(f, day)).length;
  return { events, fristen: fristenCount };
}

/**
 * Termine — localStorage pro Portal-customerId (wie Vertrags-Templates).
 * PocketBase hat keine Termin-Collection; Fristen bleiben in `portal_fristen`.
 */
export const PORTAL_KALENDER_CLASS = 'KALENDER_TILE_SEAT_OR_SKIP';

export const PORTAL_KALENDER_DAY_CLASS = 'KALENDER_DAY_OPEN_LIST';

export const PORTAL_KALENDER_SEARCH_CLASS = 'KALENDER_SEARCH_CLICK';

export const CALENDAR_SEARCH_EMPTY_LABEL = 'Keine Treffer';

export const CALENDAR_SEARCH_MAX_HITS = 24;

export type CalendarSurface = 'month' | 'day' | 'week';

export const PORTAL_KALENDER_COLLECTION = 'portal_kalender_termine';

export const PORTAL_KALENDER_PB_WRITE_RULE =
  '@request.auth.id != "" && customer_id = @request.auth.id && kanzlei_id = @request.auth.kanzlei_id';

export const PORTAL_KALENDER_PB_LIST_RULE =
  '@request.auth.id != "" && kanzlei_id = @request.auth.kanzlei_id && (customer_id = @request.auth.id || attendee_ids ~ @request.auth.id)';

export interface PortalCalendarEvent {
  id: string;
  title: string;
  date: string;
  time?: string;
  akteId?: string;
  akteTitle?: string;
  customerId?: string;
  kanzleiId?: string;
  attendeeIds?: string[];
  createdAt: string;
}

export interface CalendarDraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const KEY_PREFIX = 'arkorbit.portal.calendar.v1:';
const MAX_EVENTS = 400;

export function calendarStorageKey(customerId: string): string {
  const id = String(customerId || '').trim() || 'anon';
  return `${KEY_PREFIX}${id}`;
}

function newEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isEventShape(x: unknown): x is PortalCalendarEvent {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id.trim()) return false;
  if (typeof o.title !== 'string' || !o.title.trim()) return false;
  if (typeof o.date !== 'string' || !parseIsoDateLocal(o.date)) return false;
  if (typeof o.createdAt !== 'string') return false;
  if (o.time !== undefined && o.time !== '' && !isValidCalendarStoredTime(String(o.time))) return false;
  if (o.akteId !== undefined && typeof o.akteId !== 'string') return false;
  if (o.akteTitle !== undefined && typeof o.akteTitle !== 'string') return false;
  if (o.customerId !== undefined && typeof o.customerId !== 'string') return false;
  if (o.kanzleiId !== undefined && typeof o.kanzleiId !== 'string') return false;
  if (o.attendeeIds !== undefined && !Array.isArray(o.attendeeIds)) return false;
  if (Array.isArray(o.attendeeIds) && o.attendeeIds.some((x) => typeof x !== 'string')) return false;
  return true;
}

function parseEvents(raw: string | null): PortalCalendarEvent[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isEventShape);
  } catch {
    return [];
  }
}

function resolveStore(storage?: CalendarDraftStorage | null): CalendarDraftStorage | null {
  if (storage) return storage;
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

export function loadCalendarEvents(
  customerId: string,
  storage?: CalendarDraftStorage | null,
): PortalCalendarEvent[] {
  const store = resolveStore(storage);
  if (!store) return [];
  return parseEvents(store.getItem(calendarStorageKey(customerId)));
}

export interface SaveCalendarEventInput {
  id?: string;
  title: string;
  date: string;
  time?: string;
  akteId?: string;
  akteTitle?: string;
}

export function saveCalendarEvent(
  customerId: string,
  input: SaveCalendarEventInput,
  storage?: CalendarDraftStorage | null,
): PortalCalendarEvent {
  const store = resolveStore(storage);
  if (!store) {
    throw new Error('localStorage ist in dieser Umgebung nicht verfügbar.');
  }
  const date = sliceIsoDay(input.date);
  if (!parseIsoDateLocal(date)) {
    throw new Error('Ungültiges Datum.');
  }
  const title = String(input.title || '').trim();
  if (!title) {
    throw new Error('Titel fehlt.');
  }
  const timeRaw = String(input.time || '').trim();
  if (!isValidCalendarStoredTime(timeRaw)) {
    throw new Error('Ungültige Uhrzeit.');
  }
  const saved: PortalCalendarEvent = {
    id: String(input.id || '').trim() || newEventId(),
    title,
    date,
    createdAt: new Date().toISOString(),
  };
  if (timeRaw) saved.time = timeRaw;
  const akteId = String(input.akteId || '').trim();
  if (akteId) {
    saved.akteId = akteId;
    const akteTitle = String(input.akteTitle || '').trim();
    if (akteTitle) saved.akteTitle = akteTitle;
  }
  const all = loadCalendarEvents(customerId, store).filter((e) => e.id !== saved.id);
  all.unshift(saved);
  store.setItem(calendarStorageKey(customerId), JSON.stringify(all.slice(0, MAX_EVENTS)));
  return saved;
}

export function deleteCalendarEvent(
  customerId: string,
  eventId: string,
  storage?: CalendarDraftStorage | null,
): void {
  const store = resolveStore(storage);
  if (!store) return;
  const next = loadCalendarEvents(customerId, store).filter((e) => e.id !== eventId);
  store.setItem(calendarStorageKey(customerId), JSON.stringify(next));
}

export function eventsOnDate(events: PortalCalendarEvent[], iso: string): PortalCalendarEvent[] {
  const day = sliceIsoDay(iso);
  const matched = events.filter((e) => e.date === day);
  return matched.sort((a, b) => {
    const ta = a.time || '99:99';
    const tb = b.time || '99:99';
    if (ta !== tb) return ta.localeCompare(tb);
    return a.title.localeCompare(b.title, 'de');
  });
}

export function sanitizeKalenderSeatId(raw: string | undefined | null): string {
  return String(raw || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 80);
}

export function encodeAttendeeIds(ids: Iterable<string>, ownerId?: string): string {
  const owner = sanitizeKalenderSeatId(ownerId);
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const id = sanitizeKalenderSeatId(raw);
    if (!id || id === owner || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  if (unique.length === 0) return '';
  return `|${unique.join('||')}|`;
}

export function decodeAttendeeIds(raw: string | undefined | null): string[] {
  const text = String(raw || '').trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .map((x) => sanitizeKalenderSeatId(typeof x === 'string' ? x : ''))
        .filter(Boolean);
    }
  } catch {
    /* pipe-encoded */
  }
  return text
    .split('|')
    .map((part) => sanitizeKalenderSeatId(part))
    .filter(Boolean);
}

export function affectedSeatIds(event: Pick<PortalCalendarEvent, 'customerId' | 'attendeeIds'>): string[] {
  const ids = [
    sanitizeKalenderSeatId(event.customerId),
    ...(event.attendeeIds || []).map((id) => sanitizeKalenderSeatId(id)),
  ].filter(Boolean);
  return Array.from(new Set(ids));
}

export function eventAffectsMultipleSeats(
  event: Pick<PortalCalendarEvent, 'customerId' | 'attendeeIds'>,
): boolean {
  return affectedSeatIds(event).length > 1;
}

export function parseFlexibleDate(raw: string | undefined | null): string | null {
  const t = String(raw || '').trim();
  if (!t) return null;
  const iso = parseIsoDateLocal(t);
  if (iso) return isoDateLocal(iso);
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t);
  if (!de) return null;
  const day = de[1].padStart(2, '0');
  const month = de[2].padStart(2, '0');
  return parseIsoDateLocal(`${de[3]}-${month}-${day}`)
    ? `${de[3]}-${month}-${day}`
    : null;
}

const CALENDAR_SNAP_MINUTES = 15;

/** Nächste Viertelstunde: :00, :15, :30 oder :45. 07:07 wird 07:00 oder 07:15. */
export function snapCalendarMinutes(totalMinutes: number): number {
  const day = 24 * 60;
  const mod = ((Math.round(totalMinutes) % day) + day) % day;
  const snapped = Math.round(mod / CALENDAR_SNAP_MINUTES) * CALENDAR_SNAP_MINUTES;
  return snapped >= day ? 0 : snapped;
}

/** Vorschau während des Ziehens: die Rohminute rastet auf :00, :15, :30 oder :45. */
export function previewCalendarQuarter(rawMinutes: number): string {
  return formatCalendarMinutes(rawMinutes);
}

function calendarEventSpanMinutes(time: string | null | undefined): number {
  const raw = String(time || '').trim();
  const range = /^(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})$/.exec(raw);
  if (!range) return CALENDAR_SNAP_MINUTES;
  const start = parseFlexibleTime(range[1]);
  const end = parseFlexibleTime(range[2]);
  if (!start || !end) return CALENDAR_SNAP_MINUTES;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const dur = eh * 60 + em - (sh * 60 + sm);
  return dur > 0 ? dur : CALENDAR_SNAP_MINUTES;
}

/** Mindestens ein Viertel. Eine längere Spanne zählt weitere Viertel. */
export function calendarEventMinQuarters(time: string | null | undefined): number {
  return Math.max(1, Math.ceil(calendarEventSpanMinutes(time) / CALENDAR_SNAP_MINUTES));
}

/** Lage in der Stunde: oben die Startminute, Höhe mindestens ein Viertel. */
export function calendarEventBox(time: string | null | undefined): { topPct: number; heightPct: number } {
  const raw = String(time || '').trim();
  const range = /^(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})$/.exec(raw);
  const start = parseFlexibleTime(range ? range[1] : raw) || '00:00';
  const minute = Number(start.slice(3, 5));
  return {
    topPct: (minute / 60) * 100,
    heightPct: calendarEventMinQuarters(raw) * 25,
  };
}

/** Ziel eines Zugs: Datum des Tages und die Viertelstunde. */
export function calendarDropTarget(date: string, rawMinutes: number): { date: string; time: string } {
  const parsed = parseIsoDateLocal(date);
  return {
    date: parsed ? isoDateLocal(parsed) : '',
    time: previewCalendarQuarter(rawMinutes),
  };
}

export function formatCalendarMinutes(totalMinutes: number): string {
  const snapped = snapCalendarMinutes(totalMinutes);
  const hh = Math.floor(snapped / 60);
  const mm = snapped % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function formatMinutesExact(totalMinutes: number): string {
  const day = 24 * 60;
  const mod = ((totalMinutes % day) + day) % day;
  const hh = Math.floor(mod / 60);
  const mm = mod % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Verschiebt eine Uhrzeit und rastet den Anfang auf eine Viertelstunde. Eine Spanne behält die Dauer. */
export function shiftCalendarTime(time: string | undefined | null, deltaMinutes: number): string | null {
  const raw = String(time || '').trim();
  const range = /^(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})$/.exec(raw);
  if (range) {
    const start = parseFlexibleTime(range[1]);
    const end = parseFlexibleTime(range[2]);
    if (!start || !end) return null;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const duration = eh * 60 + em - startMin;
    const nextStart = snapCalendarMinutes(startMin + deltaMinutes);
    return `${formatMinutesExact(nextStart)}-${formatMinutesExact(nextStart + duration)}`;
  }
  const parsed = parseFlexibleTime(raw);
  if (!parsed) return null;
  const [h, m] = parsed.split(':').map(Number);
  return formatCalendarMinutes(h * 60 + m + deltaMinutes);
}

function clockToMinutes(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return h * 60 + m;
}

/** Eine einzelne Uhrzeit gilt als ein Viertel lang. */
export function calendarTimeRange(time: string | null | undefined): { start: string; end: string } {
  const raw = String(time || '').trim();
  const range = /^(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})$/.exec(raw);
  if (range) {
    const start = parseFlexibleTime(range[1]);
    const end = parseFlexibleTime(range[2]);
    if (start && end && clockToMinutes(end) - clockToMinutes(start) >= CALENDAR_SNAP_MINUTES) {
      return { start, end };
    }
  }
  const start = parseFlexibleTime(raw) || '00:00';
  const endMin = Math.min(clockToMinutes(start) + CALENDAR_SNAP_MINUTES, 23 * 60 + 45);
  return { start, end: formatMinutesExact(endMin) };
}

/**
 * Oberer Rand ändert nur den Beginn, unterer nur das Ende.
 * Beide rasten auf ein Viertel. Kürzer als ein Viertel wird nicht.
 */
export function resizeCalendarEdge(
  time: string | null | undefined,
  edge: 'start' | 'end',
  target: string,
): string | null {
  const current = calendarTimeRange(time);
  const parsedTarget = parseFlexibleTime(target);
  if (!parsedTarget) return null;
  const nextClock = previewCalendarQuarter(clockToMinutes(parsedTarget));
  let startMin = clockToMinutes(edge === 'start' ? nextClock : current.start);
  let endMin = clockToMinutes(edge === 'end' ? nextClock : current.end);
  const dayEnd = 23 * 60 + 45;
  if (edge === 'start') {
    if (endMin - startMin < CALENDAR_SNAP_MINUTES) startMin = Math.max(0, endMin - CALENDAR_SNAP_MINUTES);
  } else if (endMin - startMin < CALENDAR_SNAP_MINUTES) {
    endMin = Math.min(dayEnd, startMin + CALENDAR_SNAP_MINUTES);
  }
  if (endMin - startMin < CALENDAR_SNAP_MINUTES) return null;
  return `${formatMinutesExact(startMin)}-${formatMinutesExact(endMin)}`;
}

export type CalendarReminderMinutes = 0 | 15 | 30 | 60;

const REMINDER_OFFSET_KEY = 'arkorbit.portal.kalender.erinnerung.v1:';
const REMINDER_DISMISS_KEY = 'arkorbit.portal.kalender.erinnerung.geschlossen.v1:';

export function calendarReminderMinutes(value: number): CalendarReminderMinutes {
  if (value === 15 || value === 30 || value === 60) return value;
  return 0;
}

/** Kleines Zeichen neben der Uhrzeit, nur wenn eine Erinnerung an ist. */
export function calendarReminderMarkVisible(offsetMin: number): boolean {
  return offsetMin === 15 || offsetMin === 30 || offsetMin === 60;
}

/** Neu ohne gespeicherten Wert: 30 Minuten. Bestand ohne Wert: aus. */
export function calendarReminderOffsetOrDefault(stored: number | null, isNew: boolean): CalendarReminderMinutes {
  if (stored === 0 || stored === 15 || stored === 30 || stored === 60) return stored;
  return isNew ? 30 : 0;
}

export function calendarReminderInstant(date: string, time: string, offsetMin: number): number | null {
  if (!offsetMin) return null;
  const day = parseIsoDateLocal(date);
  const start = calendarTimeRange(time).start;
  if (!day || !start) return null;
  const [hh, mm] = start.split(':').map(Number);
  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hh, mm, 0, 0);
  return at.getTime() - offsetMin * 60 * 1000;
}

export function calendarReminderDismissKey(id: string, date: string, time: string): string {
  return `${id}|${sliceIsoDay(date)}|${calendarTimeRange(time).start}`;
}

export function calendarReminderShouldOpen(args: {
  offsetMin: number;
  date: string;
  time: string;
  nowMs: number;
  dismissed: boolean;
}): boolean {
  if (args.dismissed || !calendarReminderMinutes(args.offsetMin)) return false;
  const at = calendarReminderInstant(args.date, args.time, args.offsetMin);
  if (at == null) return false;
  return args.nowMs >= at;
}

export function calendarReminderSentence(title: string, time: string, offsetMin: CalendarReminderMinutes): string {
  const clock = calendarTimeRange(time).start;
  const lead = offsetMin === 60 ? 'eine Stunde' : `${offsetMin} Minuten`;
  const name = title.trim() || 'Termin';
  return `${name}. Termin um ${clock}. Erinnerung ${lead} vorher.`;
}

function reminderStorage(): Storage | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

function readReminderMap(seatId: string, prefix: string): Record<string, number | string> {
  const store = reminderStorage();
  const id = sanitizeKalenderSeatId(seatId);
  if (!store || !id) return {};
  try {
    const parsed = JSON.parse(store.getItem(`${prefix}${id}`) || '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, number | string>;
  } catch {
    return {};
  }
}

export function readCalendarReminderOffset(seatId: string, eventId: string): CalendarReminderMinutes {
  const raw = readReminderMap(seatId, REMINDER_OFFSET_KEY)[eventId];
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (raw == null || raw === '' || Number.isNaN(num)) return 0;
  return calendarReminderMinutes(num);
}

export function writeCalendarReminderOffset(seatId: string, eventId: string, offsetMin: CalendarReminderMinutes): void {
  const store = reminderStorage();
  const id = sanitizeKalenderSeatId(seatId);
  if (!store || !id || !eventId) return;
  const map = readReminderMap(seatId, REMINDER_OFFSET_KEY);
  map[eventId] = offsetMin;
  store.setItem(`${REMINDER_OFFSET_KEY}${id}`, JSON.stringify(map));
}

export function calendarReminderIsDismissed(seatId: string, key: string): boolean {
  const raw = readReminderMap(seatId, REMINDER_DISMISS_KEY)[key];
  return raw === 1 || raw === '1';
}

export function dismissCalendarReminder(seatId: string, key: string): void {
  const store = reminderStorage();
  const id = sanitizeKalenderSeatId(seatId);
  if (!store || !id || !key) return;
  const map = readReminderMap(seatId, REMINDER_DISMISS_KEY);
  map[key] = 1;
  store.setItem(`${REMINDER_DISMISS_KEY}${id}`, JSON.stringify(map));
}

export function assignCalendarEventLanes<T extends { id: string; time?: string | null }>(
  events: readonly T[],
  minGapMinutes = 15,
): { count: number; items: Array<T & { lane: number }> } {
  const parsed = events
    .map((ev) => {
      const bounds = calendarTimeRange(ev.time);
      const start = clockToMinutes(bounds.start);
      const end = Math.max(clockToMinutes(bounds.end), start + minGapMinutes);
      return { ev, start, end };
    })
    .sort((a, b) => a.start - b.start || a.ev.id.localeCompare(b.ev.id));
  const laneEnds: number[] = [];
  const items = parsed.map(({ ev, start, end }) => {
    let lane = laneEnds.findIndex((until) => until <= start);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    return { ...ev, lane };
  });
  return { count: Math.max(1, laneEnds.length), items };
}

export function parseFlexibleTime(raw: string | undefined | null): string | null {
  const t = String(raw || '').trim();
  if (!t) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh > 23 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export type CalendarDayTileKind = 'event' | 'frist';

export interface CalendarDayTile {
  kind: CalendarDayTileKind;
  id: string;
  title: string;
  multiSeat?: boolean;
}

export const DAY_TILE_MAX_VISIBLE = 3;

export function buildDayTiles(
  iso: string,
  events: PortalCalendarEvent[],
  fristen: CalendarFristRef[],
  maxVisible: number = DAY_TILE_MAX_VISIBLE,
): { tiles: CalendarDayTile[]; hidden: number } {
  const dayEvents = eventsOnDate(events, iso);
  const dayFristen = fristen.filter((f) => fristTouchesIsoDate(f, iso));
  const all: CalendarDayTile[] = [
    ...dayEvents.map((ev) => ({
      kind: 'event' as const,
      id: ev.id,
      title: ev.title.trim() || 'Termin',
      multiSeat: eventAffectsMultipleSeats(ev),
    })),
    ...dayFristen.map((frist, index) => ({
      kind: 'frist' as const,
      id: `frist-${sliceIsoDay(iso)}-${index}-${frist.frist_typ}`,
      title: fristLabelOnDate(frist, iso),
    })),
  ];
  return {
    tiles: all.slice(0, Math.max(0, maxVisible)),
    hidden: Math.max(0, all.length - Math.max(0, maxVisible)),
  };
}

export interface CalendarWriteContext {
  customerId: string;
  kanzleiId: string;
  visibleAkteIds: Iterable<string>;
  seatIds: Iterable<string>;
}

export function assertCalendarEventWriteAllowed(
  input: {
    customerId?: string;
    kanzleiId?: string;
    akteId?: string;
    attendeeIds?: Iterable<string>;
  },
  ctx: CalendarWriteContext,
): void {
  const seat = sanitizeKalenderSeatId(ctx.customerId);
  const kanzlei = sanitizeKalenderSeatId(ctx.kanzleiId);
  if (!seat) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: kein Sitz — Schreiben abgebrochen');
  }
  if (!kanzlei) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: keine Kanzlei — Schreiben abgebrochen');
  }
  const owner = sanitizeKalenderSeatId(input.customerId || ctx.customerId);
  if (owner !== seat) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: fremder Sitz — Schreiben verworfen');
  }
  const kid = sanitizeKalenderSeatId(input.kanzleiId || ctx.kanzleiId);
  if (kid !== kanzlei) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: fremder Mandant — Schreiben verworfen');
  }
  const akteId = String(input.akteId || '').trim();
  if (akteId) {
    const allowed = new Set(Array.from(ctx.visibleAkteIds, (id) => String(id || '').trim()).filter(Boolean));
    if (!allowed.has(akteId)) {
      throw new Error('KALENDER_TILE_SEAT_OR_SKIP: fremde Akte — Schreiben verworfen');
    }
  }
  const allowedSeats = new Set(
    Array.from(ctx.seatIds, (id) => sanitizeKalenderSeatId(id)).filter(Boolean),
  );
  for (const raw of input.attendeeIds || []) {
    const id = sanitizeKalenderSeatId(raw);
    if (!id || id === seat) continue;
    if (!allowedSeats.has(id)) {
      throw new Error('KALENDER_TILE_SEAT_OR_SKIP: fremder Sitz als Teilnehmer — Schreiben verworfen');
    }
  }
}

export function calendarEventVisibleToSeat(
  event: Pick<PortalCalendarEvent, 'customerId' | 'kanzleiId' | 'attendeeIds'>,
  seatId: string,
  kanzleiId: string,
): boolean {
  const seat = sanitizeKalenderSeatId(seatId);
  const kanzlei = sanitizeKalenderSeatId(kanzleiId);
  const evKanzlei = sanitizeKalenderSeatId(event.kanzleiId);
  if (!seat || !kanzlei || !evKanzlei || evKanzlei !== kanzlei) return false;
  return affectedSeatIds(event).includes(seat);
}

export function buildKalenderPbListFilter(customerId: string, kanzleiId: string): string {
  const cid = sanitizeKalenderSeatId(customerId);
  const kid = sanitizeKalenderSeatId(kanzleiId);
  if (!cid || !kid) return '';
  return `kanzlei_id='${kid}' && (customer_id='${cid}' || attendee_ids ~ '|${cid}|')`;
}

export function kalenderFilterIsKanzleiWide(filter: string): boolean {
  const f = String(filter || '').trim();
  if (!f) return false;
  if (/kanzlei_id\s*=/.test(f) && !/customer_id\s*=/.test(f) && !/attendee_ids/.test(f)) return true;
  return false;
}

export function calendarLocalStorageIsSoleStore(source: string): boolean {
  const s = String(source || '');
  const writesLs = /localStorage\.setItem\s*\(/.test(s);
  const writesPb =
    /portal_kalender_termine/.test(s) || /PORTAL_KALENDER_COLLECTION/.test(s);
  return writesLs && !writesPb;
}

/** Etwa ein Drittel breiter als die bisherigen 400. */
export const DAY_POPOVER_WIDTH_PX = Math.round(400 * 4 / 3);

/** Ein Viertel ist hoch genug für Uhrzeit und Titel. Vier Viertel = eine Stunde. */
export const CALENDAR_QUARTER_MIN_PX = 44;
export const CALENDAR_HOUR_MIN_PX = CALENDAR_QUARTER_MIN_PX * 4;
export const DAY_POPOVER_EST_HEIGHT_PX = 420;
export const DAY_POPOVER_GAP_PX = 8;
export const DAY_POPOVER_MARGIN_PX = 8;

export function placeDayPopover(
  anchor: { left: number; top: number; right: number; bottom: number },
  viewport: { width: number; height: number },
): { left: number; top: number } {
  // Same placement as placeSharePopover (below/above + right-align), calendar panel size.
  const width = DAY_POPOVER_WIDTH_PX;
  const height = DAY_POPOVER_EST_HEIGHT_PX;
  const gap = 6;
  const margin = DAY_POPOVER_MARGIN_PX;
  const maxLeft = Math.max(margin, viewport.width - width - margin);
  let left = anchor.right - width;
  left = Math.min(Math.max(left, margin), maxLeft);

  const spaceBelow = viewport.height - anchor.bottom - gap - margin;
  const spaceAbove = anchor.top - gap - margin;
  const preferBelow = spaceBelow >= Math.min(height, 140) || spaceBelow >= spaceAbove;
  let top = preferBelow ? anchor.bottom + gap : Math.max(margin, anchor.top - height - gap);
  const maxTop = Math.max(margin, viewport.height - height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top };
}

/** True if the panel would sit under a left chrome (Sidebar / Action-Bar). */
export function popoverOpensUnderLeftChrome(
  pos: { left: number },
  chromeRightPx: number,
  panelWidthPx: number = DAY_POPOVER_WIDTH_PX,
): boolean {
  const right = pos.left + panelWidthPx;
  return pos.left < chromeRightPx && right > 0;
}

export const DAY_HOUR_START = 7;
/** Exclusive end: labels 07:00–19:00. */
export const DAY_HOUR_END = 20;

export function formatHourLabel(hour: number): string {
  const h = Math.max(0, Math.min(23, Math.floor(hour)));
  return `${String(h).padStart(2, '0')}:00`;
}

export function eventStartsInHour(time: string | undefined | null, hour: number): boolean {
  const raw = String(time || '').trim();
  if (!raw) return false;
  return Number(calendarTimeRange(raw).start.slice(0, 2)) === hour;
}

export interface DayAgendaEntry {
  kind: CalendarDayTileKind;
  id: string;
  title: string;
  time?: string;
  multiSeat?: boolean;
}

export function buildDayAgenda(
  iso: string,
  events: PortalCalendarEvent[],
  fristen: CalendarFristRef[],
): DayAgendaEntry[] {
  const dayEvents = eventsOnDate(events, iso);
  const dayFristen = fristen.filter((f) => fristTouchesIsoDate(f, iso));
  return [
    ...dayFristen.map((frist, index) => ({
      kind: 'frist' as const,
      id: `frist-${sliceIsoDay(iso)}-${index}-${frist.frist_typ}`,
      title: fristLabelOnDate(frist, iso),
    })),
    ...dayEvents.map((ev) => ({
      kind: 'event' as const,
      id: ev.id,
      title: ev.title.trim() || 'Termin',
      time: ev.time,
      multiSeat: eventAffectsMultipleSeats(ev),
    })),
  ];
}

export interface DayHourRow {
  hour: number;
  label: string;
  events: PortalCalendarEvent[];
}

export function dayHourRangeForEvents(
  events: PortalCalendarEvent[],
  iso: string,
  startHour: number = DAY_HOUR_START,
  endHour: number = DAY_HOUR_END,
): { start: number; end: number } {
  let start = startHour;
  let end = endHour;
  for (const ev of eventsOnDate(events, iso)) {
    if (!ev.time) continue;
    const parsed = parseFlexibleTime(ev.time);
    if (!parsed) continue;
    const hour = Number(parsed.slice(0, 2));
    if (!Number.isFinite(hour)) continue;
    start = Math.min(start, hour);
    end = Math.max(end, hour + 1);
  }
  return { start: Math.max(0, start), end: Math.min(24, Math.max(start + 1, end)) };
}

export function buildDayHourRows(
  iso: string,
  events: PortalCalendarEvent[],
  startHour: number = DAY_HOUR_START,
  endHour: number = DAY_HOUR_END,
): DayHourRow[] {
  const range = dayHourRangeForEvents(events, iso, startHour, endHour);
  const dayEvents = eventsOnDate(events, iso);
  const rows: DayHourRow[] = [];
  for (let hour = range.start; hour < range.end; hour += 1) {
    rows.push({
      hour,
      label: formatHourLabel(hour),
      events: dayEvents.filter((ev) => eventStartsInHour(ev.time, hour)),
    });
  }
  return rows;
}

export function dayHourSlotCount(
  startHour: number = DAY_HOUR_START,
  endHour: number = DAY_HOUR_END,
): number {
  return Math.max(1, endHour - startHour);
}

/** Slot height from the remaining pane — not a fixed rem that overflows. */
export function computeDaySlotHeightPx(availablePx: number, slotCount: number): number {
  const n = Math.max(1, slotCount);
  const available = Math.max(0, availablePx);
  return available / n;
}

/** VERBOTEN: fixed slot height taller than the pane (hours clipped, hint over 18:00). */
export function dayGridClipsHours(args: {
  availablePx: number;
  slotCount: number;
  slotPx: number;
}): boolean {
  return args.slotPx * args.slotCount > args.availablePx + 0.5;
}

export interface CalendarDayClickOutcome {
  surface: CalendarSurface;
  selectedIso: string;
  monthVisible: boolean;
  dayViewVisible: boolean;
  createPopoverOpen: boolean;
  dayListMounted: boolean;
}

export function applyMonthDayClick(iso: string): CalendarDayClickOutcome {
  const day = sliceIsoDay(iso);
  return {
    surface: 'day',
    selectedIso: day,
    monthVisible: false,
    dayViewVisible: Boolean(day),
    createPopoverOpen: false,
    dayListMounted: Boolean(day),
  };
}

export function applyDayViewBackToMonth(selectedIso: string): CalendarDayClickOutcome {
  return {
    surface: 'month',
    selectedIso: sliceIsoDay(selectedIso),
    monthVisible: true,
    dayViewVisible: false,
    createPopoverOpen: false,
    dayListMounted: false,
  };
}

/** VERBOTEN: Monat weg, kein Tages-Liste, nur Create-Dialog. */
export function dayClickOpenedCreateOnly(outcome: CalendarDayClickOutcome): boolean {
  return !outcome.monthVisible && !outcome.dayListMounted && outcome.createPopoverOpen;
}

/** ERLAUBT: Tag-Klick öffnet Day-View mit Liste, Create-Popover bleibt zu. */
export function dayClickOpenedDayView(outcome: CalendarDayClickOutcome): boolean {
  return (
    outcome.surface === 'day' &&
    outcome.dayViewVisible &&
    outcome.dayListMounted &&
    !outcome.createPopoverOpen &&
    !outcome.monthVisible
  );
}

export type CalendarSearchMode = 'idle' | 'keyword' | 'date';

export interface CalendarSearchHit {
  id: string;
  title: string;
  date: string;
  time?: string;
  akteTitle?: string;
  multiSeat?: boolean;
}

export interface CalendarSearchResult {
  mode: CalendarSearchMode;
  query: string;
  dateIso: string;
  hits: CalendarSearchHit[];
  emptyVisible: boolean;
  jumpToDay: boolean;
}

export interface CalendarSearchContext {
  seatId: string;
  kanzleiId: string;
  visibleAkteIds?: Iterable<string>;
}

export interface CalendarSearchClickOutcome extends CalendarDayClickOutcome {
  eventId: string;
}

export function classifyCalendarSearchQuery(raw: string | undefined | null): {
  mode: CalendarSearchMode;
  dateIso: string;
  keyword: string;
} {
  const query = String(raw || '').trim();
  if (!query) {
    return { mode: 'idle', dateIso: '', keyword: '' };
  }
  const dateIso = parseFlexibleDate(query);
  if (dateIso) {
    return { mode: 'date', dateIso, keyword: '' };
  }
  return { mode: 'keyword', dateIso: '', keyword: query };
}

function toSearchHit(ev: PortalCalendarEvent): CalendarSearchHit {
  const hit: CalendarSearchHit = {
    id: ev.id,
    title: ev.title.trim() || 'Termin',
    date: ev.date,
    multiSeat: eventAffectsMultipleSeats(ev),
  };
  if (ev.time) hit.time = ev.time;
  if (ev.akteTitle) hit.akteTitle = ev.akteTitle;
  return hit;
}

function eventAllowedForSearch(ev: PortalCalendarEvent, ctx: CalendarSearchContext): boolean {
  const seat = sanitizeKalenderSeatId(ctx.seatId);
  const kanzlei = sanitizeKalenderSeatId(ctx.kanzleiId);
  if (!seat || !kanzlei) return false;
  if (!calendarEventVisibleToSeat(ev, seat, kanzlei)) return false;
  const akteId = String(ev.akteId || '').trim();
  if (!akteId) return true;
  const allowed = new Set(
    Array.from(ctx.visibleAkteIds || [], (id) => String(id || '').trim()).filter(Boolean),
  );
  if (allowed.size === 0) return true;
  return allowed.has(akteId);
}

export function searchCalendarEvents(
  raw: string | undefined | null,
  events: readonly PortalCalendarEvent[],
  ctx: CalendarSearchContext,
): CalendarSearchResult {
  const classified = classifyCalendarSearchQuery(raw);
  const query = String(raw || '').trim();
  if (classified.mode === 'idle') {
    return {
      mode: 'idle',
      query: '',
      dateIso: '',
      hits: [],
      emptyVisible: false,
      jumpToDay: false,
    };
  }

  const seat = sanitizeKalenderSeatId(ctx.seatId);
  const kanzlei = sanitizeKalenderSeatId(ctx.kanzleiId);
  if (!seat || !kanzlei) {
    return {
      mode: classified.mode,
      query,
      dateIso: classified.dateIso,
      hits: [],
      emptyVisible: true,
      jumpToDay: classified.mode === 'date',
    };
  }

  const scoped = events.filter((ev) => eventAllowedForSearch(ev, ctx));

  if (classified.mode === 'date') {
    const hits = eventsOnDate(scoped, classified.dateIso)
      .slice(0, CALENDAR_SEARCH_MAX_HITS)
      .map(toSearchHit);
    return {
      mode: 'date',
      query,
      dateIso: classified.dateIso,
      hits,
      emptyVisible: hits.length === 0,
      jumpToDay: true,
    };
  }

  const needle = classified.keyword.toLocaleLowerCase('de-AT');
  const hits = scoped
    .filter((ev) => {
      const title = (ev.title || '').toLocaleLowerCase('de-AT');
      const akte = (ev.akteTitle || '').toLocaleLowerCase('de-AT');
      return title.includes(needle) || akte.includes(needle);
    })
    .sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      const ta = a.time || '99:99';
      const tb = b.time || '99:99';
      if (ta !== tb) return ta.localeCompare(tb);
      return a.title.localeCompare(b.title, 'de');
    })
    .slice(0, CALENDAR_SEARCH_MAX_HITS)
    .map(toSearchHit);

  return {
    mode: 'keyword',
    query,
    dateIso: '',
    hits,
    emptyVisible: hits.length === 0,
    jumpToDay: false,
  };
}

export function calendarSearchEmptyIsSilent(result: CalendarSearchResult): boolean {
  return result.mode !== 'idle' && result.hits.length === 0 && !result.emptyVisible;
}

export function applyCalendarSearchHitClick(
  hit: Pick<CalendarSearchHit, 'id' | 'date'>,
): CalendarSearchClickOutcome {
  const opened = applyMonthDayClick(hit.date);
  return { ...opened, eventId: String(hit.id || '').trim() };
}

export function applyCalendarSearchDateJump(iso: string): CalendarSearchClickOutcome {
  const opened = applyMonthDayClick(iso);
  return { ...opened, eventId: '' };
}

export function searchClickOpenedCreateOnly(outcome: CalendarSearchClickOutcome): boolean {
  return dayClickOpenedCreateOnly(outcome);
}

/** ERLAUBT: Treffer-Klick öffnet den Tag (Day-View), nicht nur Create. */
export function searchClickOpenedDayOrEvent(outcome: CalendarSearchClickOutcome): boolean {
  return dayClickOpenedDayView(outcome) && Boolean(outcome.eventId);
}

export function calendarSearchSourceTouchesForbidden(source: string): boolean {
  const s = String(source || '');
  return (
    /google\s*calendar|googleapis\.com\/calendar|\bgcal\b/i.test(s) ||
    /lexcore|judicature|paragraphen-suche|JudSearch/i.test(s)
  );
}
